import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { request, jsonPut } from "@/lib/coachQueries";

/**
 * Master v5 Prompt 17 — SessionPrepPlan. Coach-only. `candidates` is the
 * ONLY source the picker UI is ever allowed to add a resource_id from —
 * the backend independently re-validates every one again on save
 * regardless, so nothing here is a trust boundary, only a UX one.
 */
export function usePrepCandidates(workspaceId, sesId, audienceKey) {
  return useQuery({
    queryKey: ["prep-candidates", workspaceId, sesId, audienceKey || null],
    enabled: !!workspaceId && !!sesId,
    queryFn: () => request(`/api/rehearsal-room/workspaces/${workspaceId}/sessions/${sesId}/prep-plan/candidates${audienceKey ? `?audience_key=${encodeURIComponent(audienceKey)}` : ""}`),
  });
}

export function usePrepPlan(workspaceId, sesId) {
  return useQuery({
    queryKey: ["prep-plan", workspaceId, sesId],
    enabled: !!workspaceId && !!sesId,
    queryFn: () => request(`/api/rehearsal-room/workspaces/${workspaceId}/sessions/${sesId}/prep-plan`),
  });
}

export function useSavePrepPlan(workspaceId, sesId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => jsonPut(`/api/rehearsal-room/workspaces/${workspaceId}/sessions/${sesId}/prep-plan`, payload),
    onSuccess: (data) => {
      qc.setQueryData(["prep-plan", workspaceId, sesId], data);
      qc.invalidateQueries({ queryKey: ["coach-today"] });
    },
  });
}

export function usePrepPreview(workspaceId, sesId, participantMemberId) {
  return useQuery({
    queryKey: ["prep-preview", workspaceId, sesId, participantMemberId],
    enabled: !!workspaceId && !!sesId && !!participantMemberId,
    queryFn: () => request(`/api/rehearsal-room/workspaces/${workspaceId}/sessions/${sesId}/prep-plan/preview?participant_member_id=${participantMemberId}`),
  });
}

// -- Master v5 Prompt 17 coach feedback round 1 — planned prep -------------
// A coach can prepare a next Session for a client BEFORE it is booked.
// Member-scoped (workspaceId + memberId), never session-scoped.
export function usePlannedPrepCandidates(workspaceId, memberId, audienceKey) {
  return useQuery({
    queryKey: ["planned-prep-candidates", workspaceId, memberId, audienceKey || null],
    enabled: !!workspaceId && !!memberId,
    queryFn: () => request(`/api/rehearsal-room/workspaces/${workspaceId}/clients/${memberId}/planned-prep/candidates${audienceKey ? `?audience_key=${encodeURIComponent(audienceKey)}` : ""}`),
  });
}

export function usePlannedPrepPlan(workspaceId, memberId) {
  return useQuery({
    queryKey: ["planned-prep-plan", workspaceId, memberId],
    enabled: !!workspaceId && !!memberId,
    queryFn: () => request(`/api/rehearsal-room/workspaces/${workspaceId}/clients/${memberId}/planned-prep`),
  });
}

export function useSavePlannedPrepPlan(workspaceId, memberId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => jsonPut(`/api/rehearsal-room/workspaces/${workspaceId}/clients/${memberId}/planned-prep`, payload),
    onSuccess: (data) => qc.setQueryData(["planned-prep-plan", workspaceId, memberId], data),
  });
}

export function usePlannedPrepPreview(workspaceId, memberId) {
  return useQuery({
    queryKey: ["planned-prep-preview", workspaceId, memberId],
    enabled: !!workspaceId && !!memberId,
    queryFn: () => request(`/api/rehearsal-room/workspaces/${workspaceId}/clients/${memberId}/planned-prep/preview`),
  });
}
